import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PlusIcon, PencilIcon, TrashIcon, FunnelIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminReasons = () => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [filterTable, setFilterTable] = useState('');
  const [formData, setFormData] = useState({
    libelle: '',
    tableRattachement: '',
    isActive: true
  });

  // Récupérer les motifs
  const { data: reasonsData, isLoading } = useQuery(
    ['reasons', filterTable],
    async () => {
      const token = localStorage.getItem('accessToken');
      const params = filterTable ? `?tableRattachement=${filterTable}` : '';
      const response = await axios.get(`${API_URL}/reasons${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data;
    }
  );

  // Mutation pour créer
  const createMutation = useMutation(
    async (data) => {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${API_URL}/reasons`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Motif créé avec succès');
        queryClient.invalidateQueries('reasons');
        closeModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la création');
      }
    }
  );

  // Mutation pour mettre à jour
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(`${API_URL}/reasons/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Motif modifié avec succès');
        queryClient.invalidateQueries('reasons');
        closeModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la modification');
      }
    }
  );

  // Mutation pour supprimer
  const deleteMutation = useMutation(
    async (id) => {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/reasons/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    {
      onSuccess: () => {
        toast.success('Motif supprimé avec succès');
        queryClient.invalidateQueries('reasons');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
      }
    }
  );

  const openCreateModal = () => {
    setEditingReason(null);
    setFormData({ libelle: '', tableRattachement: '', isActive: true });
    setShowModal(true);
  };

  const openEditModal = (reason) => {
    setEditingReason(reason);
    setFormData({
      libelle: reason.libelle,
      tableRattachement: reason.tableRattachement,
      isActive: reason.isActive
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingReason(null);
    setFormData({ libelle: '', tableRattachement: '', isActive: true });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.libelle || !formData.tableRattachement) {
      toast.error('Le libellé et la table de rattachement sont requis');
      return;
    }

    if (editingReason) {
      updateMutation.mutate({ id: editingReason.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (reason) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${reason.libelle}" ?`)) {
      deleteMutation.mutate(reason.id);
    }
  };

  const getTableLabel = (table) => {
    switch (table) {
      case 'requests':
        return 'Motifs de demande';
      case 'requeststreatment':
        return 'Motifs de rejet';
      default:
        return table;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Motifs
            </h1>
            <p className="text-gray-600">
              Gérez les motifs de demande et de rejet
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="btn-primary flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nouveau motif
          </button>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-card p-4">
        <div className="flex items-center space-x-4">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <select
            value={filterTable}
            onChange={(e) => setFilterTable(e.target.value)}
            className="form-input max-w-xs"
          >
            <option value="">Toutes les tables</option>
            <option value="requests">Motifs de demande</option>
            <option value="requeststreatment">Motifs de rejet</option>
          </select>
        </div>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Libellé
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Catégorie
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Utilisation
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {reasonsData?.map((reason) => (
              <tr key={reason.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-gray-900">
                    {reason.libelle}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`badge ${
                    reason.tableRattachement === 'requests' 
                      ? 'badge-primary' 
                      : 'badge-warning'
                  }`}>
                    {getTableLabel(reason.tableRattachement)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`badge ${reason.isActive ? 'badge-success' : 'badge-gray'}`}>
                    {reason.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {(reason._count?.requests || 0) + (reason._count?.requestsTreatment || 0)} utilisation(s)
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => openEditModal(reason)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Modifier"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(reason)}
                      className="text-red-600 hover:text-red-900"
                      title="Supprimer"
                      disabled={(reason._count?.requests || 0) + (reason._count?.requestsTreatment || 0) > 0}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={closeModal} />

            <div className="inline-block bg-white rounded-lg shadow-xl transform transition-all sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {editingReason ? 'Modifier le motif' : 'Nouveau motif'}
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="form-label">
                        Libellé <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.libelle}
                        onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                        className="form-input"
                        placeholder="Ex: Départ à la retraite, Documents incomplets..."
                        required
                      />
                    </div>

                    <div>
                      <label className="form-label">
                        Catégorie <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.tableRattachement}
                        onChange={(e) => setFormData({ ...formData, tableRattachement: e.target.value })}
                        className="form-input"
                        required
                        disabled={!!editingReason}
                      >
                        <option value="">Sélectionner une catégorie</option>
                        <option value="requests">Motifs de demande</option>
                        <option value="requeststreatment">Motifs de rejet</option>
                      </select>
                      {editingReason && (
                        <p className="text-xs text-gray-500 mt-1">
                          La catégorie ne peut pas être modifiée
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="form-checkbox"
                        />
                        <span className="ml-2 text-sm text-gray-700">Actif</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="btn-primary sm:ml-3"
                  >
                    {(createMutation.isLoading || updateMutation.isLoading) && (
                      <LoadingSpinner size="small" className="mr-2" />
                    )}
                    {editingReason ? 'Modifier' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="btn-secondary mt-3 sm:mt-0"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReasons;
